export type MapBuiltInToolId =
  | 'select'
  | 'inspect'
  | 'draw_point'
  | 'draw_line'
  | 'draw_polygon'
  | 'measure_distance'
  | 'street_distance'
  | 'clear_drawings';

export type MapDrawGeometryKind = 'point' | 'line' | 'polygon';

export type MapDrawnGeometry =
  | { geometry_kind: 'point'; coordinates: [number, number] }
  | { geometry_kind: 'line'; coordinates: Array<[number, number]> }
  | { geometry_kind: 'polygon'; coordinates: Array<[number, number]> };

export type MapDrawingOverlayFeatureRole = 'geometry' | 'preview' | 'vertex';

export type MapBuiltInToolDefinition = {
  id: MapBuiltInToolId;
  label: string;
  kind: 'selection' | 'inspection' | 'drawing' | 'measurement' | 'utility';
  geometry_kind?: MapDrawGeometryKind | null;
  requires_capability?: 'routing' | null;
};

export const MAP_BUILT_IN_TOOL_DEFINITIONS: MapBuiltInToolDefinition[] = [
  { id: 'select', label: 'Select', kind: 'selection' },
  { id: 'inspect', label: 'Inspect', kind: 'inspection' },
  { id: 'draw_point', label: 'Draw Point', kind: 'drawing', geometry_kind: 'point' },
  { id: 'draw_line', label: 'Draw Line', kind: 'drawing', geometry_kind: 'line' },
  { id: 'draw_polygon', label: 'Draw Polygon', kind: 'drawing', geometry_kind: 'polygon' },
  { id: 'measure_distance', label: 'Measure Distance', kind: 'measurement', geometry_kind: 'line' },
  { id: 'street_distance', label: 'Street Distance', kind: 'measurement', geometry_kind: 'line', requires_capability: 'routing' },
  { id: 'clear_drawings', label: 'Clear Drawings', kind: 'utility' },
];

export const DEFAULT_VISIBLE_MAP_TOOL_IDS: MapBuiltInToolId[] = [
  'select',
  'inspect',
  'draw_point',
  'draw_line',
  'draw_polygon',
  'clear_drawings',
];

export type GeometryBounds = [number, number, number, number];

export const MAP_DRAW_VERTEX_HIT_TARGET_PX = 12;
export const MAP_DRAW_POLYGON_CLOSE_TOLERANCE_PX = 14;

export const isDrawingToolId = (toolId: MapBuiltInToolId) =>
  toolId === 'draw_point' ||
  toolId === 'draw_line' ||
  toolId === 'draw_polygon' ||
  toolId === 'measure_distance' ||
  toolId === 'street_distance';

export const isLineLikeDrawingToolId = (toolId: MapBuiltInToolId) =>
  toolId === 'draw_line' || toolId === 'measure_distance' || toolId === 'street_distance';

export const getDrawingToolGeometryKind = (toolId: MapBuiltInToolId): MapDrawGeometryKind | null => {
  if (toolId === 'draw_point') return 'point';
  if (isLineLikeDrawingToolId(toolId)) return 'line';
  if (toolId === 'draw_polygon') return 'polygon';
  return null;
};

export const canCompleteDrawingDraft = (toolId: MapBuiltInToolId, draft: Array<[number, number]>) =>
  (isLineLikeDrawingToolId(toolId) && draft.length >= 2) || (toolId === 'draw_polygon' && draft.length >= 3);

export const completeDrawingDraft = (
  toolId: MapBuiltInToolId,
  draft: Array<[number, number]>,
): MapDrawnGeometry | null => {
  if (toolId === 'draw_point') {
    return draft[0] ? { geometry_kind: 'point', coordinates: draft[0] } : null;
  }
  if (isLineLikeDrawingToolId(toolId) && draft.length >= 2) {
    return { geometry_kind: 'line', coordinates: [...draft] };
  }
  if (toolId === 'draw_polygon' && draft.length >= 3) {
    return { geometry_kind: 'polygon', coordinates: [...draft] };
  }
  return null;
};

export const isGeometryCompatibleWithTool = (toolId: MapBuiltInToolId, geometry: MapDrawnGeometry | null) => {
  if (!geometry) return false;
  const expected = getDrawingToolGeometryKind(toolId);
  return expected != null && geometry.geometry_kind === expected;
};

export const buildInteractiveDrawingGeometry = ({
  activeToolId,
  drawnGeometry,
  draftCoordinates,
}: {
  activeToolId: MapBuiltInToolId;
  drawnGeometry: MapDrawnGeometry | null;
  draftCoordinates: Array<[number, number]>;
}): MapDrawnGeometry | null => {
  if (drawnGeometry) {
    return drawnGeometry;
  }
  if (activeToolId === 'draw_point') {
    return draftCoordinates[0] ? { geometry_kind: 'point', coordinates: draftCoordinates[0] } : null;
  }
  if (isLineLikeDrawingToolId(activeToolId)) {
    return draftCoordinates.length > 0 ? { geometry_kind: 'line', coordinates: [...draftCoordinates] } : null;
  }
  if (activeToolId === 'draw_polygon') {
    if (draftCoordinates.length >= 3) {
      return { geometry_kind: 'polygon', coordinates: [...draftCoordinates] };
    }
    return draftCoordinates.length > 0 ? { geometry_kind: 'line', coordinates: [...draftCoordinates] } : null;
  }
  return null;
};

export const updateCoordinateAtIndex = (
  coordinates: Array<[number, number]>,
  index: number,
  nextPoint: [number, number],
) =>
  coordinates.map((coordinate, coordinateIndex) =>
    coordinateIndex === index ? [nextPoint[0], nextPoint[1]] : [coordinate[0], coordinate[1]],
  ) as Array<[number, number]>;

export const updateDrawnGeometryVertex = (
  geometry: MapDrawnGeometry,
  index: number,
  nextPoint: [number, number],
): MapDrawnGeometry => {
  if (geometry.geometry_kind === 'point') {
    return { geometry_kind: 'point', coordinates: [nextPoint[0], nextPoint[1]] };
  }
  return {
    geometry_kind: geometry.geometry_kind,
    coordinates: updateCoordinateAtIndex(geometry.coordinates, index, nextPoint),
  } as MapDrawnGeometry;
};

const buildOverlayFeature = (
  role: MapDrawingOverlayFeatureRole,
  geometry: Record<string, unknown>,
  properties?: Record<string, unknown>,
) => ({
  type: 'Feature',
  properties: {
    role,
    ...(properties ?? {}),
  },
  geometry,
});

export const buildDrawingOverlayFeatureCollection = ({
  geometry,
  previewSegments = [],
  showVertexHandles,
}: {
  geometry: MapDrawnGeometry | null;
  previewSegments?: Array<Array<[number, number]>>;
  showVertexHandles: boolean;
}) => {
  const features: Array<Record<string, unknown>> = [];
  const hasRenderableGeometry =
    geometry != null &&
    (geometry.geometry_kind === 'point' ||
      (geometry.geometry_kind === 'line' && geometry.coordinates.length >= 2) ||
      (geometry.geometry_kind === 'polygon' && geometry.coordinates.length >= 3));
  if (geometry) {
    if (hasRenderableGeometry) {
      features.push(buildOverlayFeature('geometry', mapDrawnGeometryToGeoJson(geometry)));
    }
    if (showVertexHandles) {
      const vertexCoordinates =
        geometry.geometry_kind === 'point' ? [geometry.coordinates] : geometry.coordinates;
      vertexCoordinates.forEach((coordinate, index) => {
        features.push(
          buildOverlayFeature(
            'vertex',
            {
              type: 'Point',
              coordinates: coordinate,
            },
            {
              vertex_index: index,
              geometry_kind: geometry.geometry_kind,
            },
          ),
        );
      });
    }
  }
  previewSegments
    .filter(segment => segment.length >= 2)
    .forEach((segment, index) => {
      features.push(
        buildOverlayFeature('preview', {
          type: 'LineString',
          coordinates: segment,
        }, { preview_index: index }),
      );
    });
  return {
    type: 'FeatureCollection',
    features,
  };
};

const isCoordinate = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length >= 2 &&
  typeof value[0] === 'number' &&
  Number.isFinite(value[0]) &&
  typeof value[1] === 'number' &&
  Number.isFinite(value[1]);

const collectCoordinates = (value: unknown, sink: Array<[number, number]>) => {
  if (isCoordinate(value)) {
    sink.push([value[0], value[1]]);
    return;
  }
  if (!Array.isArray(value)) {
    return;
  }
  value.forEach(entry => collectCoordinates(entry, sink));
};

export const computeMapGeometryBounds = (geometry?: Record<string, unknown> | null): GeometryBounds | undefined => {
  if (!geometry || typeof geometry !== 'object') return undefined;
  const coordinates = (geometry as { coordinates?: unknown }).coordinates;
  const points: Array<[number, number]> = [];
  collectCoordinates(coordinates, points);
  if (points.length === 0) return undefined;
  let minLng = points[0][0];
  let minLat = points[0][1];
  let maxLng = points[0][0];
  let maxLat = points[0][1];
  points.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });
  return [minLng, minLat, maxLng, maxLat];
};

export const mapDrawnGeometryToGeoJson = (geometry: MapDrawnGeometry): Record<string, unknown> => {
  if (geometry.geometry_kind === 'point') {
    return { type: 'Point', coordinates: geometry.coordinates };
  }
  if (geometry.geometry_kind === 'line') {
    return { type: 'LineString', coordinates: geometry.coordinates };
  }
  const ring = geometry.coordinates.length > 0 ? geometry.coordinates : [];
  const closedRing =
    ring.length >= 3 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])
      ? [...ring, ring[0]]
      : ring;
  return { type: 'Polygon', coordinates: [closedRing] };
};

export const mapDrawnGeometryToFeatureCollection = (geometry: MapDrawnGeometry | null) => ({
  type: 'FeatureCollection',
  features: geometry
    ? [
        {
          type: 'Feature',
          properties: {
            geometry_kind: geometry.geometry_kind,
          },
          geometry: mapDrawnGeometryToGeoJson(geometry),
        },
      ]
    : [],
});

export const mapDrawnGeometryBounds = (geometry: MapDrawnGeometry | null): GeometryBounds | null =>
  geometry ? computeMapGeometryBounds(mapDrawnGeometryToGeoJson(geometry)) ?? null : null;

const pointInBounds = (point: [number, number], bounds: GeometryBounds) =>
  point[0] >= bounds[0] && point[0] <= bounds[2] && point[1] >= bounds[1] && point[1] <= bounds[3];

const boundsIntersect = (left: GeometryBounds, right: GeometryBounds) =>
  left[0] <= right[2] && left[2] >= right[0] && left[1] <= right[3] && left[3] >= right[1];

const pointInPolygon = (point: [number, number], polygon: Array<[number, number]>) => {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersects =
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / ((yj - yi) || Number.EPSILON) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
};

const firstCoordinateFromGeometry = (geometry?: Record<string, unknown> | null): [number, number] | null => {
  if (!geometry || typeof geometry !== 'object') return null;
  const points: Array<[number, number]> = [];
  collectCoordinates((geometry as { coordinates?: unknown }).coordinates, points);
  return points[0] ?? null;
};

export const featureMatchesDrawnGeometry = ({
  geometry,
  operator,
  drawnGeometry,
}: {
  geometry?: Record<string, unknown> | null;
  operator: 'within_drawn_geometry' | 'intersects_drawn_geometry';
  drawnGeometry: MapDrawnGeometry | null;
}) => {
  if (!geometry || !drawnGeometry) {
    return false;
  }
  const geometryBounds = computeMapGeometryBounds(geometry);
  const drawnBounds = mapDrawnGeometryBounds(drawnGeometry);
  if (!geometryBounds || !drawnBounds) {
    return false;
  }
  if (drawnGeometry.geometry_kind === 'polygon') {
    const point = firstCoordinateFromGeometry(geometry);
    if (point) {
      const within = pointInPolygon(point, drawnGeometry.coordinates);
      return operator === 'within_drawn_geometry' ? within : within || boundsIntersect(geometryBounds, drawnBounds);
    }
  }
  return operator === 'within_drawn_geometry'
    ? pointInBounds([geometryBounds[0], geometryBounds[1]], drawnBounds) &&
        pointInBounds([geometryBounds[2], geometryBounds[3]], drawnBounds)
    : boundsIntersect(geometryBounds, drawnBounds);
};

export const buildFeatureRowsCsv = (rows: Array<Record<string, unknown>>) => {
  if (rows.length === 0) {
    return '';
  }
  const headers = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach(key => set.add(key));
      return set;
    }, new Set<string>()),
  );
  const escape = (value: unknown) => {
    const text = value == null ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  return [headers.join(','), ...rows.map(row => headers.map(header => escape(row[header])).join(','))].join('\n');
};
